type EnvVarConfig = {
  required?: boolean;
  fallback?: string;
  validate?: (value: string) => boolean;
  errorMessage?: string;
};

type EnvVarConfigs = {
  [key: string]: EnvVarConfig;
};

/**
 * Validates environment variables based on provided configuration
 * @param configs Configuration for environment variables
 * @throws Error if any required environment variable is missing or invalid
 */
export function validateEnvVars(configs: EnvVarConfigs): void {
  for (const [varName, config] of Object.entries(configs)) {
    const value = import.meta.env[varName];
    if (config.required && !value) {
      throw new Error(`Missing required environment variable: ${varName}`);
    }
    if (config.validate && value && !config.validate(value)) {
      throw new Error(config.errorMessage || `Invalid value for environment variable: ${varName}`);
    }
  }
}

/**
 * Type-safe environment variable getter
 * @param varName Environment variable name
 * @returns The environment variable value
 */
export function getEnvVar(varName: string): string {
  const value = import.meta.env[varName];
  if (!value) {
    throw new Error(`Missing environment variable: ${varName}`);
  }
  return value;
}

/**
 * Type-safe environment variable getter with fallback
 * @param varName Environment variable name
 * @param fallback Fallback value if environment variable is not set
 * @returns The environment variable value or fallback
 */
export function getEnvVarWithFallback(varName: string, fallback: string): string {
  return import.meta.env[varName] || fallback;
}
